import { useCallback, useEffect, useState, useRef } from 'react';
import { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import AutoScroll from 'embla-carousel-auto-scroll';
import { CarouselMarqueeProps } from './marqueeTypes';
import { Marketing } from 'lib/templates/Feature.BOKF.model';

/**
 * Shared carousel logic for Marquee components.
 * Returns carousel state, embla refs, and navigation handlers.
 */

export function useMarqueeCarousel(props: CarouselMarqueeProps) {
  const options: EmblaOptionsType = { loop: true, duration: 25 };
  const marquees =
    (props?.fields?.Marquees as Marketing.DataTemplates.CarouselMarqueeSlide[])?.length > 0
      ? props?.fields?.Marquees
      : props?.fields?.children;
  const width =
    props.params?.EnableFluidWidth === '1' ? 'w-full max-w-[100vw]' : 'w-full container-wide';

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    Autoplay({
      delay: props.fields?.AutoScrollDelay?.value,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
    }),
    AutoScroll({ playOnInit: false, speed: 0 }),
  ]);

  const [enableAutoScroll, setEnableAutoScroll] = useState<boolean>();
  const [enableCarousel, setEnableCarousel] = useState<boolean>();
  const [isPlaying, setIsPlaying] = useState<boolean>();
  const loginMarqueeRef = useRef<HTMLInputElement>(null);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    const resetOrStop =
      autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop;
    resetOrStop();
  }, []);

  const toggleAutoplay = useCallback(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;
    const playOrStop = autoScroll.isPlaying() ? autoScroll.stop : autoScroll.play;
    playOrStop();
  }, [emblaApi]);

  useEffect(() => {
    setEnableAutoScroll(props?.fields?.EnableAutoScroll?.value);
    if (!enableAutoScroll) emblaApi?.plugins()?.autoplay?.stop();
  }, [emblaApi, enableAutoScroll, props?.fields?.EnableAutoScroll?.value]);

  useEffect(() => {
    setEnableCarousel(props?.fields?.EnableCarousel?.value);
    if (!enableCarousel) emblaApi?.plugins()?.autoplay?.stop();
  }, [emblaApi, enableCarousel, props?.fields?.EnableCarousel?.value]);

  useEffect(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll;
    if (!autoScroll) return;
    setIsPlaying(autoScroll.isPlaying());
    emblaApi
      .on('autoScroll:play', () => setIsPlaying(true))
      .on('autoScroll:stop', () => setIsPlaying(false))
      .on('reInit', () => setIsPlaying(autoScroll.isPlaying()));
  }, [emblaApi, isPlaying]);

  return {
    marquees,
    width,
    emblaRef,
    emblaApi,
    enableAutoScroll,
    enableCarousel,
    isPlaying,
    loginMarqueeRef,
    onNavButtonClick,
    toggleAutoplay,
  };
}
